import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fileUrl, pdfUrl, evidencePackUrl, dampReportUrl, openSigned, exportPdf } from "@/lib/api";
import { toast } from "sonner";
import { AuthImage } from "@/components/AuthImage";
import { Lightbox } from "@/components/Lightbox";
import { PropertyMap } from "@/components/PropertyMap";
import { FloorPlan3DView } from "@/components/FloorPlan3DView";
import { GENERAL_CHECKS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Download,
  Pencil,
  Loader2,
  MapPin,
  User,
  Calendar,
  FileText,
  Home,
  Check,
  X,
  Minus,
  AlertTriangle,
  DoorOpen,
  ShieldCheck,
  Droplets,
  Wrench,
  Package,
  Box,
  ShieldAlert,
} from "lucide-react";

function PhotoGrid({ photos }) {
  const [lightbox, setLightbox] = useState(null);
  if (!photos?.length) return null;
  return (
    <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3">
      {photos.map((p) => (
        <button
          type="button"
          key={p.id}
          onClick={() => setLightbox(fileUrl(p.id))}
          className="overflow-hidden rounded-md border border-slate-200 text-left"
          data-testid={`detail-photo-${p.id}`}
        >
          <AuthImage fileId={p.id} w={400} alt="site" className="aspect-[4/3] w-full object-cover transition-transform hover:scale-105" />
          {p.lat != null && (
            <div className="flex items-center gap-1 bg-slate-900/80 px-2 py-1 text-[10px] font-medium text-white">
              <MapPin className="h-3 w-3 text-accent" />
              {Number(p.lat).toFixed(4)}, {Number(p.lng).toFixed(4)}
              {p.captured_at && <span className="ml-auto opacity-80">{new Date(p.captured_at).toLocaleDateString("en-GB")}</span>}
            </div>
          )}
        </button>
      ))}
      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}

export default function VisitDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const handleExportPdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    const tId = toast.loading("Preparing report… photo-heavy jobs can take up to a minute.");
    try {
      const filename = `${v?.visit_type || "visit"}-coordination-${(v?.postcode || "").replace(/\s/g, "")}.pdf`;
      await exportPdf(id, { filename });
      toast.success("Report ready — downloading now.", { id: tId });
    } catch (e) {
      toast.error("The report is taking longer than usual. Please try again in a moment.", { id: tId });
    } finally {
      setPdfBusy(false);
    }
  };

  useEffect(() => {
    api.get(`/visits/${id}`).then((r) => setV(r.data));
  }, [id]);

  if (!v)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-accent" />
      </div>
    );

  const installed = (v.measures || []).filter((m) => m.installed);
  const checks = GENERAL_CHECKS[v.visit_type] || Object.keys(v.general || {});
  const actions = v.actions || [];
  const title = v.visit_type === "mid" ? "Mid Coordination" : "Post Coordination";

  const renderVal = (val) => {
    if (val === true) return <span className="inline-flex items-center gap-1 font-semibold text-accent"><Check className="h-4 w-4" /> Yes</span>;
    if (val === false) return <span className="inline-flex items-center gap-1 font-semibold text-destructive"><X className="h-4 w-4" /> No</span>;
    if (val === "N/A" || val === "" || val == null) return <span className="inline-flex items-center gap-1 text-slate-400"><Minus className="h-4 w-4" /> N/A</span>;
    return <span className="font-semibold text-slate-800">{String(val)}</span>;
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <button onClick={() => navigate("/")} className="flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </button>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Badge className={`font-bold uppercase ${v.visit_type === "mid" ? "bg-blue-100 text-blue-700 hover:bg-blue-100" : "bg-accent/15 text-accent hover:bg-accent/15"}`}>{title}</Badge>
            <Badge variant="outline" className={v.status === "completed" ? "border-accent text-accent" : "border-slate-300 text-slate-500"}>
              {v.status === "completed" ? "Completed" : "Draft"}
            </Badge>
          </div>
          <h1 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{v.job_address}</h1>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate(`/visits/${id}/edit`)} className="field-lg border-slate-300 font-bold" data-testid="edit-btn">
            <Pencil className="mr-2 h-4 w-4" /> Edit
          </Button>
          <Button className="field-lg bg-primary font-bold" onClick={handleExportPdf} disabled={pdfBusy} data-testid="pdf-btn">
            {pdfBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            {pdfBusy ? "Preparing…" : "Export PDF"}
          </Button>
          <a href={evidencePackUrl(id)} onClick={(e) => { e.preventDefault(); openSigned(evidencePackUrl, id); }} data-testid="evidence-pack-btn">
            <Button className="field-lg bg-accent font-bold hover:bg-accent/90"><Package className="mr-2 h-4 w-4" /> Evidence Pack</Button>
          </a>
        </div>
      </div>

      {/* Property verification */}
      {v.front_photo?.id && (
        <Card className="border-slate-200 p-6 shadow-sm">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><ShieldCheck className="h-5 w-5 text-accent" /> Property Verification</h2>
          <div className="mt-3 max-w-md">
            <PhotoGrid photos={[v.front_photo]} />
          </div>
        </Card>
      )}

      {/* Details */}
      <Card className="border-slate-200 p-6 shadow-sm">
        <h2 className="font-heading text-lg font-bold text-slate-900">Visit Details</h2>
        <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
          <Detail icon={User} label="Completed by" value={`${v.completed_by}${v.completed_by_role ? ` · ${v.completed_by_role}` : ""}`} />
          <Detail icon={Calendar} label="Visit date" value={`${v.visit_date || "—"}${v.visit_time ? ` · ${v.visit_time}` : ""}`} />
          <Detail icon={MapPin} label="Postcode" value={v.postcode} />
          <Detail icon={FileText} label="Client / Scheme" value={v.client_scheme} />
          <Detail icon={FileText} label="Job reference" value={v.job_reference || "—"} />
          <Detail icon={User} label="Resident" value={v.resident_present ? v.resident_name || "Present" : "Not present"} />
          <Detail icon={Home} label="Property type" value={v.property_type || "—"} />
          <Detail icon={Home} label="Wall type" value={v.wall_type || "—"} />
        </div>
        <div className="mt-5">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Property location</p>
          <div className="mt-1.5">
            <PropertyMap lat={v.front_photo?.lat} lng={v.front_photo?.lng} />
          </div>
        </div>
      </Card>

      {/* General checks */}
      {checks.length > 0 && (
        <Card className="border-slate-200 p-6 shadow-sm">
          <h2 className="font-heading text-lg font-bold text-slate-900">General Checks</h2>
          <div className="mt-4 space-y-2">
            {checks.map((c) => {
              const isImprovement = c.toLowerCase().includes("certificate");
              return (
              <div key={c} className="border-b border-slate-100 py-2 last:border-0">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">{c}</span>
                  {v.general?.[c] === true ? (
                    <span className="inline-flex items-center gap-1 font-semibold text-accent"><Check className="h-4 w-4" /> Confirmed</span>
                  ) : isImprovement ? (
                    <span className="inline-flex items-center gap-1 font-bold text-amber-600" data-testid={`gcheck-improve-${c.slice(0, 12)}`}><AlertTriangle className="h-4 w-4" /> Room for improvement</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 font-bold text-destructive" data-testid={`gcheck-fail-${c.slice(0, 12)}`}><X className="h-4 w-4" /> Not confirmed</span>
                  )}
                </div>
                {v.general?.[c] !== true && v.general_notes?.[c] && (
                  <p className="mt-1 rounded-md bg-amber-50 px-3 py-2 text-sm italic text-slate-700">{v.general_notes[c]}</p>
                )}
              </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Measures */}
      <div className="space-y-4">
        <h2 className="font-heading text-xl font-bold text-slate-900">Measures Installed ({installed.length})</h2>
        {installed.length === 0 && <Card className="border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">No measures marked as installed.</Card>}
        {installed.map((m) => (
          <Card key={m.measure_id} className="border-slate-200 p-6 shadow-sm" data-testid={`detail-measure-${m.code}`}>
            <div className="flex items-center gap-2">
              <h3 className="font-heading text-lg font-bold text-slate-900">{m.name}</h3>
              <span className="rounded bg-accent/15 px-1.5 py-0.5 text-[11px] font-bold text-accent">{m.code}</span>
            </div>
            {(m.installer_name || m.installer_tm) && (
              <p className="mt-1 text-sm text-slate-500" data-testid={`detail-installer-${m.code}`}>
                Installer: <span className="font-semibold text-slate-700">{m.installer_name || "—"}</span>
                {m.installer_tm && <> · TrustMark: <span className="font-semibold text-slate-700">{m.installer_tm}</span></>}
              </p>
            )}
            <div className="mt-4 space-y-2">
              {Object.entries(m.answers || {}).map(([label, val]) => (
                <div key={label} className="flex items-start justify-between gap-4 border-b border-slate-100 py-2 last:border-0">
                  <span className="text-sm text-slate-600">{label}</span>
                  <div className="shrink-0 text-right text-sm">{renderVal(val)}</div>
                </div>
              ))}
            </div>

            {/* Rooms */}
            {m.rooms?.length > 0 && (
              <div className="mt-5 space-y-3">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500"><DoorOpen className="h-4 w-4" /> Rooms ({m.rooms.length})</p>
                {m.rooms.map((r) => (
                  <div key={r.id} className="rounded-md border border-slate-200 p-3">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-bold text-slate-900">{r.type || "Room"}</span>
                      {r.height && <Badge variant="outline" className="border-slate-300 text-slate-600">{r.height} m</Badge>}
                      {r.is_wet_room && <Badge variant="outline" className="border-blue-300 text-blue-700">Wet room</Badge>}
                      {r.is_wc_only && <Badge variant="outline" className="border-slate-300 text-slate-600">W/C only</Badge>}
                      {r.no_door && <Badge variant="outline" className="border-slate-300 text-slate-600">No door</Badge>}
                      {r.background_ventilation && <Badge variant="outline" className={r.background_ventilation === "Not sufficient" ? "border-destructive text-destructive" : "border-accent text-accent"}>{r.background_ventilation}</Badge>}
                    </div>
                    {r.notes && <p className="mt-1 text-sm text-slate-500">{r.notes}</p>}
                    {[["Door undercut", r.undercut_photos], ["Trickle vent", r.trickle_photos], ["dMEV / extract", r.dmev_photos], ["No sink present (W/C)", r.no_sink_photos], ["Room height", r.height_photos], ["Room", r.photos]].map(
                      ([lbl, ph]) => (ph?.length > 0 ? (
                        <div key={lbl} className="mt-2">
                          <p className="text-[11px] font-bold uppercase text-slate-400">{lbl}</p>
                          <PhotoGrid photos={ph} />
                        </div>
                      ) : null)
                    )}
                  </div>
                ))}
              </div>
            )}

            {m.photos?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Evidence photos</p>
                <PhotoGrid photos={m.photos} />
              </div>
            )}
            {m.radiator_photos?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Radiator photos ({m.radiator_photos.length})</p>
                <PhotoGrid photos={m.radiator_photos} />
              </div>
            )}
            {m.fcaps_photos?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">F-Caps photos</p>
                <PhotoGrid photos={m.fcaps_photos} />
              </div>
            )}
            {m.thermal_photos?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Thermal imaging photos ({m.thermal_photos.length})</p>
                <PhotoGrid photos={m.thermal_photos} />
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Floor plan */}
      {(v.floor_plan?.rooms?.length > 0) && (
        <Card className="border-slate-200 p-6 shadow-sm" data-testid="detail-floorplan">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-slate-900"><Box className="h-5 w-5 text-teal-600" /> Floor Plan &amp; Damp/Mould Map</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {v.floor_plan.snapshot_2d?.id && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">2D layout</p>
                <AuthImage fileId={v.floor_plan.snapshot_2d.id} alt="2D floor plan" className="mt-2 w-full rounded-md border border-slate-200 bg-white object-contain" />
              </div>
            )}
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">3D model — drag to rotate</p>
              <div className="mt-2">
                <FloorPlan3DView rooms={v.floor_plan.rooms} markers={v.floor_plan.markers || []} height={300} />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Faults / actions */}
      {actions.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-heading text-xl font-bold text-slate-900">Faults & Actions ({actions.length})</h2>
          {actions.map((a, i) => (
            <Card key={a.id} className="border-amber-300 bg-amber-50/40 p-6 shadow-sm" data-testid={`detail-action-${a.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-bold text-amber-700"><AlertTriangle className="h-4 w-4" /> Fault #{i + 1}</div>
                <Badge variant="outline" className={a.remediated ? "border-emerald-500 text-emerald-600" : a.status === "Closed" ? "border-accent text-accent" : "border-amber-400 text-amber-700"}>{a.remediated ? "Remediated" : a.status}</Badge>
              </div>
              <p className="mt-2 text-sm font-semibold text-slate-800">{a.description}</p>
              <div className="mt-1 text-xs text-slate-500">
                Assigned to {a.assigned_person ? `${a.assigned_person} (${a.assigned_to})` : a.assigned_to}{a.location ? ` · ${a.location}` : ""}{a.due_date ? ` · Due ${a.due_date}` : ""}
              </div>
              <PhotoGrid photos={a.photos} />
              {a.remediation_photos?.length > 0 && (
                <div className="mt-3">
                  <p className="text-[11px] font-bold uppercase text-emerald-700">Remediation evidence{a.remediated_at ? ` · ${new Date(a.remediated_at).toLocaleDateString("en-GB")}` : ""}</p>
                  <PhotoGrid photos={a.remediation_photos} />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Damp & mould */}
      {(v.damp_mould || []).length > 0 && (
        <div className="space-y-4">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-slate-900"><Droplets className="h-5 w-5 text-orange-600" /> Damp & Mould ({v.damp_mould.length})</h2>
          {v.damp_mould.map((d, i) => (
            <Card key={d.id} className="border-orange-300 bg-orange-50/40 p-6 shadow-sm" data-testid={`detail-damp-${d.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-bold text-slate-900">{d.location || `Finding #${i + 1}`}</span>
                {d.type && <Badge variant="outline" className="border-orange-300 text-xs text-orange-600">{d.type}</Badge>}
                {d.severity && <Badge variant="outline" className="border-orange-400 text-orange-700">{d.severity} severity</Badge>}
                <Badge variant="outline" className="border-red-400 text-red-700">Raised as fault</Badge>
              </div>
              {d.notes && <p className="mt-2 text-sm text-slate-600">{d.notes}</p>}
              <PhotoGrid photos={d.photos || []} />
            </Card>
          ))}
        </div>
      )}


      {/* Defect remediation */}
      {(v.remediations || []).length > 0 && (
        <div className="space-y-4">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-slate-900"><Wrench className="h-5 w-5 text-emerald-600" /> Defect Remediation Works ({v.remediations.length})</h2>
          {v.remediations.map((r, i) => (
            <Card key={r.id} className="border-emerald-300 bg-emerald-50/40 p-6 shadow-sm" data-testid={`detail-remediation-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm font-bold text-emerald-700">
                <Wrench className="h-4 w-4" /> Remediation #{i + 1}
                {r.measure_code && <Badge variant="outline" className="border-emerald-400 text-emerald-700">{r.measure_code === "Other" ? (r.measure_other || "Other") : r.measure_code}</Badge>}
                {r.location && <span className="font-normal text-slate-500">{r.location}</span>}
              </div>
              <div className="mt-3">
                <p className="text-[11px] font-bold uppercase text-destructive">Defect — what was wrong</p>
                <p className="text-sm text-slate-700">{r.defect || "—"}</p>
              </div>
              <div className="mt-2">
                <p className="text-[11px] font-bold uppercase text-accent">Action taken — how it was fixed</p>
                <p className="text-sm text-slate-700">{r.action_taken || "—"}</p>
              </div>
              {r.before_photos?.length > 0 && (
                <div className="mt-3"><p className="text-[11px] font-bold uppercase text-destructive">Before</p><PhotoGrid photos={r.before_photos} /></div>
              )}
              {r.after_photos?.length > 0 && (
                <div className="mt-3"><p className="text-[11px] font-bold uppercase text-accent">After</p><PhotoGrid photos={r.after_photos} /></div>
              )}
            </Card>
          ))}
        </div>
      )}

      {v.overall_notes && (
        <Card className="border-slate-200 p-6 shadow-sm">
          <h2 className="font-heading text-lg font-bold text-slate-900">Overall Notes & Actions</h2>
          <p className="mt-3 whitespace-pre-wrap text-sm text-slate-600">{v.overall_notes}</p>
        </Card>
      )}

      {/* Signatures */}
      {(v.engineer_signature?.file_id || v.resident_signature?.file_id) && (
        <Card className="border-slate-200 p-6 shadow-sm">
          <h2 className="font-heading text-lg font-bold text-slate-900">Sign-off</h2>
          <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
            {[["Engineer / Coordinator", v.engineer_signature], ["Resident", v.resident_signature]].map(([label, sig]) =>
              sig?.file_id ? (
                <div key={label}>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
                  <AuthImage fileId={sig.file_id} alt="signature" className="mt-2 h-20 rounded border border-slate-200 bg-white object-contain p-1" />
                  <p className="mt-1 text-xs text-slate-500">{sig.name} · {new Date(sig.signed_at).toLocaleString("en-GB")}</p>
                </div>
              ) : null
            )}
          </div>
        </Card>
      )}

      {/* Client sign-off */}
      {(v.client_signature?.file_id || v.client_comments || v.client_satisfied) && (
        <Card className="border-emerald-200 bg-emerald-50/30 p-6 shadow-sm" data-testid="detail-client-signoff">
          <h2 className="font-heading text-lg font-bold text-slate-900">Client Sign-off</h2>
          <p className="mt-3 text-sm text-slate-700">
            {v.visit_type === "post" ? "Satisfied with the completed works:" : "Happy with the works going ahead:"}{" "}
            <span className={v.client_satisfied ? "font-bold text-emerald-700" : "font-semibold text-slate-400"}>
              {v.client_satisfied ? "Yes — confirmed" : "Not confirmed"}
            </span>
          </p>
          {v.client_comments && (
            <div className="mt-3">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Client comments / feedback</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{v.client_comments}</p>
            </div>
          )}
          {v.client_signature?.file_id && (
            <div className="mt-4">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Client signature</p>
              <AuthImage fileId={v.client_signature.file_id} alt="client signature" className="mt-2 h-20 rounded border border-slate-200 bg-white object-contain p-1" />
              <p className="mt-1 text-xs text-slate-500">{v.client_signature.name} · {new Date(v.client_signature.signed_at).toLocaleString("en-GB")}</p>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-sm font-semibold text-slate-800">{value}</p>
      </div>
    </div>
  );
}
